import React from 'react';
import * as R from 'ramda';
import moment from 'moment-timezone';
import {
  BooleanInput,
  Edit,
  Error,
  FormDataConsumer,
  FormWithRedirect,
  ImageField,
  ImageInput,
  Loading,
  minValue,
  number,
  NumberInput,
  regex,
  required,
  SelectInput,
  TextInput,
  Toolbar,
  useQuery,
  SelectArrayInput,
} from 'react-admin';
import { Box, Button, makeStyles, Typography, Link } from '@material-ui/core';
import { DateRange, TrendingUp } from '@material-ui/icons';

import { DateInput } from 'resources/common/DatePickers';
import { authServer } from 'auth/keycloak';
import {
  b2bProjectTypes,
  cohortTimezones,
  formats,
  languages,
  tracksB2b,
} from 'data';
import mutators from './mutators';
import CohortB2bSaveButton from './CohortB2bSaveButton';
import AutomaticAttendanceInput from 'resources/common/AutomaticAttendanceInput';
import CohortSettingsInput, {
  cohortSettingsToFormValue,
} from 'resources/common/CohortSettingsInput';
import { hasNewStudentPortal } from 'utils/hasNewStudentPortal';
import CohortB2bTimetableInputs from '../CohortB2bTimetableInputs';
import { hasDoubleSessions } from '../b2bSessions';

const CohortTitle = ({ record }) =>
  `${record.campus.toUpperCase()} ${record.track.toUpperCase()} ${record.format.toUpperCase()} ${
    record.start_date.split('T')[0]
  } ${record.language.toUpperCase()}`;

const validateRequired = [required('Required')];
const validateNumbers = [number(), minValue(0)];
const validateRequiredNumbers = R.concat(validateRequired, validateNumbers);
const validateUrl = regex(
  /^(ftp|http|https):\/\/[^ "]+$/,
  'Invalid URL. Be sure to include the `http://` or `https://` protocol to the URL',
);

const validateCohortEdit = (values) => {
  const errors = {};

  const emptyPartTimeDays = R.pathEq(['timetable', 'days'], '0000000', values);
  if (emptyPartTimeDays) {
    errors.timetable = {
      days: ['Required'],
    };
  }

  const wrongStartDate =
    moment(values.start_date).toDate() > moment(values.end_date).toDate();
  if (wrongStartDate) {
    errors.start_date = ['The start date cannot be later than the end date'];
    errors.end_date = ['The end date cannot be earlier than the start date'];
  }

  const wrongPartTimeStartDate = R.allPass([
    R.propEq('format', 'pt'),
    R.has('start_date'),
    R.hasPath(['timetable', 'days']),
    (values) => {
      const startDay = moment(values.start_date).day();
      const partTimeDays = R.path(['timetable', 'days'], values);
      return partTimeDays[startDay] !== '1';
    },
  ])(values);
  if (wrongPartTimeStartDate) {
    errors.start_date = ['The cohort must start on a selected class day'];
  }

  const wrongDiscount =
    R.path(['price', 'discount'], values) > R.path(['price', 'amount'], values);
  if (wrongDiscount) {
    errors.price = {
      discount: ['Cannot be larger than the cohort price'],
    };
  }

  return errors;
};

const GROUPS = {
  pm: '8b9f3f55-1147-409e-adb0-7e5f1f6a9d03',
  teacher: '4a7f515d-cd31-48bc-b360-4eca24baf47f',
  b2b: '6af1ee35-b99f-4faa-8bd1-71422eacd084',
};

const createGroupQuery = (pagination, groups) => {
  const groupIds = Array.isArray(groups)
    ? groups.map((group) => GROUPS[group])
    : [GROUPS[groups]];

  return {
    type: 'getList',
    resource: 'users',
    payload: {
      filter: { group: groupIds, with_groups: false },
      pagination,
    },
  };
};
const useStyles = makeStyles({
  dropZone: {
    borderStyle: 'dashed',
    borderColor: 'rgba(0, 0, 0, 0.54)',
  },
});

const CohortB2bEditForm = (props) => {
  const pms = useQuery(createGroupQuery({ page: 1, perPage: 100 }, 'pm'));
  const teachers = useQuery(
    createGroupQuery({ page: 1, perPage: 200 }, 'teacher'),
  );
  const b2b = useQuery(createGroupQuery({ page: 1, perPage: 200 }, 'b2b'));

  const filteredUsers = (teachers.data || []).filter((teacher) =>
    (b2b.data || []).some((b2bUser) => b2bUser.id === teacher.id),
  );
  const classes = useStyles();

  const { data: emails, loading: emailsLoading } = useQuery({
    type: 'getList',
    resource: 'emails',
    payload: {
      pagination: {
        page: 1,
        perPage: 250,
      },
      sort: {
        field: 'name',
        order: 'ASC',
      },
      filter: {
        family: 'course_welcome_b2b',
      },
    },
  });

  const cohortIdForSettings = props.record?.id || props.record?._id;
  const usesNewStudentPortal = hasNewStudentPortal(
    cohortIdForSettings,
    props.record?.start_date,
  );
  const {
    data: newStudentPortalSettings,
    loading: newStudentPortalSettingsLoading,
  } = useQuery(
      {
        type: 'getOne',
        resource: 'cohort_settings',
        payload: { id: cohortIdForSettings },
      },
      { enabled: usesNewStudentPortal && Boolean(cohortIdForSettings) },
    );

  if (pms.loading || teachers.loading)
    return (
      <Box p="16px">
        <Loading />
      </Box>
    );

  if (pms.error || teachers.error)
    return (
      <Box p="16px">
        <Error />
      </Box>
    );

  if (usesNewStudentPortal && newStudentPortalSettingsLoading) {
    return (
      <Box p="16px">
        <Loading />
      </Box>
    );
  }

  const fieldsConfig = {
    track: {
      validate: validateRequired,
      disabled: false,
    },
    format: {
      validate: validateRequired,
      disabled: false,
    },
    timezone: {
      validate: validateRequired,
      disabled: true,
    },
    language: {
      validate: validateRequired,
      disabled: !authServer.isAllRoles(['pm', 'b2b']),
    },
    start_date: {
      validate: validateRequired,
      disabled: !authServer.isAllRoles(['pm', 'b2b']),
    },
    end_date: {
      validate: validateRequired,
      disabled: !authServer.isAllRoles(['pm', 'b2b']),
    },
    'timetable.days': {
      validate: validateRequired,
      disabled: !authServer.isAllRoles(['pm', 'b2b']),
    },
    'timetable.time.start_time': {
      validate: validateRequired,
      disabled: !authServer.isAllRoles(['pm', 'b2b']),
    },
    'timetable.time.end_time': {
      validate: validateRequired,
      disabled: !authServer.isAllRoles(['pm', 'b2b']),
    },
    'timetable.time.weekend_start_time': {
      validate: validateRequired,
      disabled: !authServer.isAllRoles(['pm', 'b2b']),
    },
    'timetable.time.weekend_end_time': {
      validate: validateRequired,
      disabled: !authServer.isAllRoles(['pm', 'b2b']),
    },
    'staff.pm': {
      validate: validateRequired,
      disabled: !authServer.isAllRoles(['pm', 'b2b']),
    },
    'prework.title': {
      validate: validateRequired,
      disabled: !authServer.isAllRoles(['pm', 'b2b']),
    },
    'prework.openedx': {
      validate: validateRequired,
      disabled: !authServer.isAllRoles(['pm', 'b2b']),
    },
    'course.title': {
      validate: validateRequired,
      disabled: !authServer.isAllRoles(['pm', 'b2b']),
    },
    'course.openedx': {
      validate: validateRequired,
      disabled: !authServer.isAllRoles(['pm', 'b2b']),
    },
    'career_services.title': {
      validate: R.hasPath(['record', 'career_services'], props)
        ? validateRequired
        : [],
      disabled: false,
    },
    'career_services.openedx': {
      validate: R.hasPath(['record', 'career_services'], props)
        ? validateRequired
        : [],
      disabled: false,
    },
    feature_flags: {
      validate: [],
      disabled: !authServer.isAllRoles(['admin']),
    },
    'staff.teachers': {
      validate: [],
      disabled: !authServer.isAllRoles(['pm', 'b2b']),
    },
    zoom_url: {
      validate: [
        R.pipe(
          R.defaultTo(''),
          R.trim,
          R.ifElse(
            R.isEmpty,
            R.always(undefined),
            R.ifElse(
              R.allPass([
                R.startsWith('https://ironhack.zoom.us/j/'),
                R.pipe(
                  R.replace('https://ironhack.zoom.us/j/', ''),
                  R.test(/^\d+$/),
                ),
              ]),
              R.always(undefined),
              R.always(
                'The URL must start with https://ironhack.zoom.us/j/ followed by a meeting ID. For example: https://ironhack.zoom.us/j/1234567890',
              ),
            ),
          ),
        ),
      ],
      parse: R.pipe(R.defaultTo(''), R.trim),
    },
    presentation_video: {
      validate: [],
      parse: R.ifElse(R.isNil, R.identity, R.trim),
    },
    slack_url: {
      validate: [],
      parse: R.ifElse(R.isNil, R.identity, R.trim),
    },
    accredible_design_id: {
      validate: [
        R.pipe(
          R.defaultTo(''),
          R.trim,
          R.ifElse(R.isEmpty, R.always(undefined), R.always(undefined)),
        ),
      ],
      parse: R.pipe(
        R.defaultTo(''),
        R.trim,
        R.ifElse(R.isEmpty, R.always(null), R.identity),
      ),
    },
  };

  return (
    <FormWithRedirect
      {...props}
      mutators={mutators}
      validate={validateCohortEdit}
      initialValues={
        usesNewStudentPortal
          ? {
              cohort_settings: cohortSettingsToFormValue(newStudentPortalSettings),
            }
          : undefined
      }
      render={(formProps) => {
        const usesNewStudentPortalInForm = hasNewStudentPortal(
          formProps.record?.id,
          formProps.record?.start_date,
        );
        const isTimetableDisabled =
          formProps.record?.status !== 'not_started' ||
          !authServer.isAllRoles(['pm', 'b2b']);
        const defaultSessions =
          Number(formProps.record?.sessions) ||
          (hasDoubleSessions(formProps.record) ? 2 : 1);
        return (
          <form>
            <Box display="block" p="1.5em">
              <Typography variant="h5" gutterBottom>
                Cohort
              </Typography>
              <Box display={{ sm: 'block', md: 'flex' }}>
                <Box flex={1} mr={{ md: '2em' }}>
                  <SelectInput
                    label="Track"
                    source="track"
                    choices={tracksB2b}
                    variant="standard"
                    fullWidth
                    {...fieldsConfig.track}
                  />
                </Box>
                <Box flex={1} mr={{ md: '2em' }}>
                  <SelectInput
                    label="Format"
                    source="format"
                    choices={formats}
                    variant="standard"
                    fullWidth
                    {...fieldsConfig.format}
                  />
                </Box>
                <Box flex={1} mr={{ md: '2em' }}>
                  <SelectInput
                    label="Timezone"
                    source="timezone"
                    choices={cohortTimezones}
                    variant="standard"
                    fullWidth
                    {...fieldsConfig.timezone}
                  />
                </Box>
                <Box flex={1}>
                  <SelectInput
                    label="Language"
                    source="language"
                    choices={languages}
                    variant="standard"
                    fullWidth
                    {...fieldsConfig.language}
                  />
                </Box>
              </Box>
              <Box display={{ sm: 'block', md: 'flex' }}>
                <Box flexBasis="calc((100% - 6em)/4)" mr={{ md: '2em' }}>
                  <TextInput
                    label="Company"
                    source="company"
                    validate={validateRequired}
                    variant="standard"
                    fullWidth
                  />
                </Box>
                <Box flexBasis="calc((100% - 6em)/4)" mr={{ md: '2em' }}>
                  <SelectInput
                    label="Project Type"
                    source="project_type"
                    choices={b2bProjectTypes}
                    validate={validateRequired}
                    variant="standard"
                    fullWidth
                  />
                </Box>
                <Box flexBasis="calc((100% - 6em)/4)">
                  <NumberInput
                    label="Hours per week"
                    source="week_hours"
                    validate={validateRequiredNumbers}
                    defaultValue={0}
                    variant="standard"
                    fullWidth
                  />
                </Box>
              </Box>
              <Box mb="3em" />
              <Typography variant="h5" gutterBottom>
                Dates
              </Typography>
              <Box display={{ sm: 'block', md: 'flex' }}>
                <Box flex={1} mr={{ md: '2em' }}>
                  <DateInput
                    label="Start Date"
                    source="start_date"
                    options={{
                      format: 'Do MMMM YYYY',
                      openTo: 'year',
                      views: ['year', 'month', 'date'],
                    }}
                    variant="standard"
                    fullWidth
                    {...fieldsConfig['start_date']}
                  />
                </Box>
                <Box flex={1}>
                  <DateInput
                    label="End Date"
                    source="end_date"
                    options={{
                      format: 'Do MMMM YYYY',
                      openTo: 'year',
                      views: ['year', 'month', 'date'],
                    }}
                    variant="standard"
                    fullWidth
                    {...fieldsConfig['end_date']}
                  />
                </Box>
              </Box>
              <FormDataConsumer>
                {({ formData }) => (
                  <CohortB2bTimetableInputs
                    defaultSessions={defaultSessions}
                    disabled={isTimetableDisabled}
                    format={formData.format}
                  />
                )}
              </FormDataConsumer>
              <Typography variant="h5" gutterBottom>
                Staff
              </Typography>
              <Box display={{ sm: 'block', md: 'flex' }}>
                <Box flex={1} mr={{ md: '2em' }}>
                  <SelectInput
                    label="PM"
                    source="staff.pm"
                    format={(pm) =>
                      pm ? `${pm.email}#${pm.first_name}#${pm.last_name}` : ''
                    }
                    parse={R.pipe(
                      R.defaultTo(''),
                      R.split('#'),
                      R.zipObj(['email', 'first_name', 'last_name']),
                    )}
                    choices={R.map(
                      (pm) => {
                        pm.info = `${pm.email}#${pm.first_name}#${pm.last_name}`;
                        return pm;
                      },
                      R.defaultTo([], pms.data),
                    )}
                    variant="standard"
                    optionText={(pm) => `${pm.first_name} ${pm.last_name}`}
                    optionValue="info"
                    fullWidth
                    {...fieldsConfig['staff.pm']}
                  />
                </Box>
                <Box flex={1}>
                  <NumberInput
                    source="staff.ta_amount"
                    label="Number of TAs"
                    variant="standard"
                    min={0}
                    step={1}
                    fullWidth
                    {...fieldsConfig['staff.ta_amount']}
                  />
                </Box>
              </Box>
              <Box display={{ sm: 'block', md: 'flex' }}>
                <Box flex={1}>
                  <SelectArrayInput
                    label="Lead Teachers"
                    source="staff.teachers"
                    choices={R.map(
                      (teacher) => {
                        teacher.info = `${teacher.email}#${teacher.first_name}#${teacher.last_name}`;
                        return teacher;
                      },
                      R.defaultTo([], filteredUsers),
                    )}
                    format={R.pipe(
                      R.defaultTo([]),
                      R.map((teacher) =>
                        teacher
                          ? `${teacher.email}#${teacher.first_name}#${teacher.last_name}`
                          : '',
                      ),
                    )}
                    parse={R.pipe(
                      R.defaultTo([]),
                      R.map(
                        R.pipe(
                          R.defaultTo(''),
                          R.split('#'),
                          R.zipObj(['email', 'first_name', 'last_name']),
                        ),
                      ),
                    )}
                    variant="standard"
                    optionText={(teacher) =>
                      teacher
                        ? `${teacher.first_name} ${teacher.last_name}`
                        : ''
                    }
                    optionValue="info"
                    fullWidth
                    {...fieldsConfig['staff.teachers']}
                  />
                </Box>
              </Box>
              <Box>
                <Typography variant="h5" gutterBottom>
                  Courses
                </Typography>
                <Box display={{ sm: 'block', md: 'flex' }} mt="10px">
                  <Box flex={1} flexDirection="row" mr={{ md: '2em' }}>
                    {R.hasPath(['record', 'prework'], props) && (
                      <Box>
                        <Box mb="10px">
                          <Typography variant="h6" gutterBottom>
                            Prework
                          </Typography>
                        </Box>
                        <Box>
                          <TextInput
                            source="prework.title"
                            label="Student Portal Title"
                            variant="standard"
                            fullWidth
                            {...fieldsConfig['prework.title']}
                          />
                        </Box>
                        <Box>
                          <TextInput
                            source="prework.openedx"
                            label="Github Course ID"
                            variant="standard"
                            fullWidth
                            {...fieldsConfig['prework.openedx']}
                          />
                        </Box>
                      </Box>
                    )}
                  </Box>
                  <Box flex={1} flexDirection="row" mr={{ md: '2em' }}>
                    <Box mb="10px">
                      <Typography variant="h6" gutterBottom>
                        Main Course
                      </Typography>
                    </Box>
                    <Box>
                      <TextInput
                        source="course.title"
                        label="Student Portal Title"
                        variant="standard"
                        fullWidth
                        {...fieldsConfig['course.title']}
                      />
                    </Box>
                    <Box>
                      <TextInput
                        source="course.openedx"
                        label="Github Course ID"
                        variant="standard"
                        fullWidth
                        {...fieldsConfig['course.openedx']}
                      />
                    </Box>
                  </Box>
                  <Box flex={1} flexDirection="row" mr={{ md: '2em' }}>
                    <Box>
                      <Box mb="10px">
                        <Typography variant="h6" gutterBottom>
                          Career Services
                        </Typography>
                      </Box>
                      <Box>
                        <TextInput
                          source="career_services.title"
                          label="Student Portal Title"
                          variant="standard"
                          fullWidth
                          {...fieldsConfig['career_services.title']}
                        />
                      </Box>
                      <Box>
                        <TextInput
                          source="career_services.openedx"
                          label="Github Course ID"
                          variant="standard"
                          fullWidth
                          {...fieldsConfig['career_services.openedx']}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
              <Box>
                <Typography variant="h5" gutterBottom>
                  Cohort specifics
                </Typography>
                <Box
                  display={{ sm: 'block', md: 'flex' }}
                  alignItems="flex-start"
                >
                  {authServer.isRoles(['admin']) && !emailsLoading && (
                    <Box flexBasis="32%" mr={{ md: '2em' }}>
                      <SelectInput
                        label="Course Welcome email"
                        source="course.email_templates.welcome_email_template_id"
                        choices={R.pipe(
                          R.map((template) =>
                            R.assoc(
                              'name',
                              `${template.name}${
                                template.language
                                  ? `- ${template.language.toUpperCase()}`
                                  : ''
                              }`,
                              template,
                            ),
                          ),
                          R.sortBy(R.prop('name')),
                        )(emails)}
                        variant="standard"
                        fullWidth
                      />
                    </Box>
                  )}
                  <Box flexBasis="68%">
                    <TextInput
                      label="Link to FAQs"
                      source="faq_url"
                      validate={validateUrl}
                      variant="standard"
                      fullWidth
                    />
                  </Box>
                </Box>
                <Box
                  display={{ sm: 'block', md: 'flex' }}
                  alignItems="flex-end"
                >
                  <Box flexBasis="calc((100% - 4em)/2)">
                    <TextInput
                      label="Text for client banner"
                      source="banner.copy"
                      variant="standard"
                      fullWidth
                    />
                  </Box>
                </Box>
                <Box>
                  <ImageInput
                    source="images"
                    classes={classes}
                    label="Client logos"
                    accept="image/*"
                    multiple
                    labelMultiple="Drop files or click to select them (Images). The maximum size is 3MB"
                    maxSize={3000000}
                  >
                    <ImageField source="src" title="title" />
                  </ImageInput>
                </Box>
                <Box display={{ sm: 'block', md: 'flex' }}>
                  <Box flexBasis="calc((100% - 4em)/2)">
                    <TextInput
                      source="slack_url"
                      label="Slack URL"
                      variant="standard"
                      fullWidth
                      {...fieldsConfig.slack_url}
                    />
                  </Box>
                </Box>
                <Box display={{ sm: 'block', md: 'flex' }}>
                  <Box flexBasis="calc((100% - 4em)/2)">
                    <TextInput
                      source="presentation_video"
                      label="Onboarding Video"
                      variant="standard"
                      fullWidth
                      helperText="Test the link in an incognito window to make sure that students will be able to see the video"
                      {...fieldsConfig.presentation_video}
                    />
                  </Box>
                </Box>
                <Box display={{ sm: 'block', md: 'flex' }}>
                  <Box flexBasis="calc((100% - 4em)/2)">
                    <TextInput
                      label="Accredible design ID"
                      source="external_ids.accredible_design_id"
                      variant="standard"
                      fullWidth
                      {...fieldsConfig.accredible_design_id}
                    />
                  </Box>
                </Box>
                <Box display={{ sm: 'block', md: 'flex' }}>
                  <Box flexBasis="calc((100% - 4em)/2)">
                    <TextInput
                      source="zoom_url"
                      label="Zoom URL"
                      variant="standard"
                      fullWidth
                      {...fieldsConfig.zoom_url}
                    />
                  </Box>
                  <Box ml={{ md: '4em' }} mt="14px">
                    <FormDataConsumer>
                      {({ formData }) => (
                        <AutomaticAttendanceInput
                          label="Automatic Attendance"
                          source="feature_flags.automatic_attendance_enabled"
                          defaultValue={false}
                          variant="standard"
                          record={formData}
                          {...fieldsConfig.feature_flags}
                        />
                      )}
                    </FormDataConsumer>
                  </Box>
                </Box>
                <Box
                  width="100%"
                  color="grey.600"
                  display="flex"
                  alignItems="center"
                >
                  <Typography variant="body2">
                    By Adding the Zoom URL: Students will be able to access it
                    from the SP. If the course runs in Github, the recordings
                    will be automatically added to a Module called General Info{' '}
                    {'>'} Class Recordings{' '}
                    <Link
                      href="https://ironhack.notion.site/Adding-Zoom-to-a-cohort-265396f8d1fe80a8af86dacbce42e709?source=copy_link"
                      color="primary"
                      target="_blank"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'baseline',
                        verticalAlign: 'baseline',
                      }}
                    >
                      <span style={{ lineHeight: '1.43' }}>More info</span>
                      <TrendingUp
                        style={{
                          fontSize: '14px',
                          marginLeft: '4px',
                          marginBottom: '2px',
                        }}
                      />
                    </Link>
                  </Typography>
                </Box>
                <Box
                  width="100%"
                  mt="10px"
                  color="grey.600"
                  display="flex"
                  alignItems="center"
                >
                  <Typography variant="body2">
                    By activating Automatic Attendance, Students will be asked
                    to login with their Ironhack account, we'll review the zoom
                    sessions and add the information to the Attendance tab in CT{' '}
                    <Link
                      href="https://www.notion.so/ironhack/CT-Automatic-Attendance-265396f8d1fe809e8ce2d0d6ef1d22e1?source=copy_link"
                      color="primary"
                      target="_blank"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'baseline',
                        verticalAlign: 'baseline',
                      }}
                    >
                      <span style={{ lineHeight: '1.43' }}>More info</span>
                      <TrendingUp
                        style={{
                          fontSize: '14px',
                          marginLeft: '4px',
                          marginBottom: '2px',
                        }}
                      />
                    </Link>
                  </Typography>
                </Box>
                <Box mb="3em" mt="20px" display="flex" alignItems="baseline">
                  <Box width="120px">
                    {usesNewStudentPortalInForm ? (
                      <CohortSettingsInput
                        cohortId={formProps.record?.id || formProps.record?._id}
                      />
                    ) : (
                      <BooleanInput
                        label="Multilanguage active"
                        source="feature_flags.multilanguage_enabled"
                        defaultValue={false}
                        variant="standard"
                        fullWidth
                        {...fieldsConfig.feature_flags}
                      />
                    )}
                  </Box>
                  <Box
                    width="100%"
                    ml="7em"
                    color="grey.600"
                    display="flex"
                    alignItems="center"
                  >
                    <Typography variant="body2">
                      Allow students to switch the Student Portal to English,
                      Spanish, German, French or Catalan{' '}
                      <Link
                        href="https://ironhack.notion.site/Campus-Tools-Multilanguage-Guide-265396f8d1fe80909f37eabd2f88e4df"
                        color="primary"
                        target="_blank"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'baseline',
                          verticalAlign: 'baseline',
                        }}
                      >
                        <span style={{ lineHeight: '1.43' }}>More info</span>
                        <TrendingUp
                          style={{
                            fontSize: '14px',
                            marginLeft: '4px',
                            marginBottom: '2px',
                          }}
                        />
                      </Link>
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
            <Toolbar>
              {authServer.isAllRoles(['pm', 'b2b']) && (
                <Box display="flex" justifyContent="space-between" width="100%">
                  <CohortB2bSaveButton
                    record={formProps.record}
                    basePath={formProps.basePath}
                    undoable={true}
                    invalid={formProps.invalid}
                    saving={formProps.saving}
                    resource="cohorts"
                    submitOnEnter={false}
                    handleSubmitWithRedirect={
                      formProps.handleSubmitWithRedirect
                    }
                  />
                  {authServer.isAllRoles(['pm', 'b2b']) && (
                    <FormDataConsumer>
                      {({ formData }) => {
                        const calculateCohortDates = () => {
                          const mutator = R.path(
                            ['form', 'mutators', 'cohortDatesMutator'],
                            formProps,
                          );
                          mutator();
                        };
                        return (
                          <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<DateRange />}
                            onClick={calculateCohortDates}
                            disabled={
                              !R.allPass([
                                R.has('format'),
                                R.has('start_date'),
                                R.complement(R.propEq)('format', 'other'),
                              ])(formData)
                            }
                          >
                            Calculate Cohort Dates
                          </Button>
                        );
                      }}
                    </FormDataConsumer>
                  )}
                </Box>
              )}
            </Toolbar>
          </form>
        );
      }}
    />
  );
};

const CohortB2bEdit = ({ classes, ...props }) => (
  <Edit title={<CohortTitle />} actions={null} undoable={false} {...props}>
    <CohortB2bEditForm redirect="show" />
  </Edit>
);

export default CohortB2bEdit;
